import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLatest } from '@/shared/hooks/useLatest';
import { useObjectCategoryPicker } from '@/shared/lib/objectCategoryNav';
import { Button } from '@/shared/ui/button';
import { FieldLabel } from '@/shared/ui/form-field/FieldLabel';
import { InlineFieldLabel } from '@/shared/ui/form-field/InlineFieldLabel';
import { FieldMessage } from '@/shared/ui/form-field/FieldMessage';
import {
  DEFAULT_LIST_PICKER_MODE,
  DEFAULT_PICK_MODAL_WIDTH,
  DEFAULT_SELECTOR_PAGE_SIZE,
} from '../constants';
import { AsyncSelectorPickModal } from '../pick-modal/AsyncSelectorPickModal';
import { usePagedAsyncSuggestions } from '../search/usePagedAsyncSuggestions';
import { AsyncSelectorField } from './AsyncSelectorField';
import { AsyncSelectorPopover } from './AsyncSelectorPopover';
import type { AsyncSelectorProps } from './AsyncSelector.types';
import type { ListPagePickerComponent, ListPickerMode } from '../types';
import { useAsyncSelectorLegacyNav } from './hooks/useAsyncSelectorLegacyNav';
import { useAsyncSelectorPopover } from './hooks/useAsyncSelectorPopover';
import { useAsyncSelectorSelection } from './hooks/useAsyncSelectorSelection';

// Výchozí styl před prvním změřením: popover je v DOM (kvůli změření offsetů),
// ale skrytý, aby neblikl na (0,0). useLayoutEffect ho synchronně dopočítá před paintem.
const HIDDEN_POPOVER_STYLE: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  visibility: 'hidden',
  zIndex: 'var(--phx-z-overlay)',
};

/**
 * Selector dle designového handoffu „Anatomie selectoru": vybrané záznamy jako
 * plnohodnotné řádky uvnitř pole, vyhledávání v popoveru, akce + (nový záznam,
 * jen prázdné pole) a ⋯ (vždy; otevírá list page dané object category ve
 * výběrovém režimu), řádkové akce ✎/× a centrální × pro multi se ≥ 2 záznamy.
 */
export function AsyncSelector<TItem>({
  mode,
  label,
  helperText,
  error,
  selected,
  getKey,
  columns,
  renderSelected,
  renderSelectedSecondary,
  isItemSelected,
  search,
  queryKey,
  onChange,
  placeholder,
  testId,
  required,
  disabled = false,
  layout = 'stacked',
  labelWidth = 104,
  objectCategory,
  form,
  resolveByIds,
  listPicker = DEFAULT_LIST_PICKER_MODE,
  pickModalTitle,
  pickModalWidth = DEFAULT_PICK_MODAL_WIDTH,
  renderPickToolbar,
  defaultPageSize = DEFAULT_SELECTOR_PAGE_SIZE,
  filterResetKey,
  trigger = 'field',
  buttonLabel,
  buttonVariant = 'default',
  buttonSize = 'default',
  confirmLabel,
  lockedIds,
  addMode = false,
}: AsyncSelectorProps<TItem>) {
  const { t } = useTranslation('asyncSelector');
  const fieldId = useId();
  const popupId = useId();

  const isButton = trigger === 'button';
  // button + multi drží rozpracovaný (draft) výběr a potvrzuje ho tlačítkem „Vybrat".
  const workingMode = isButton && mode === 'multi';

  // ⋯ picker: má-li ObjectCategory zaregistrovanou native ListPage (postupná náhrada legacy),
  // otevři ji ve výběrovém režimu místo legacy Magic listu. Jinak zůstává `listPicker` z definice.
  const nativePicker = useObjectCategoryPicker(objectCategory) as
    | ListPagePickerComponent
    | undefined;
  const effectiveListPicker: ListPickerMode = nativePicker
    ? { listPage: nativePicker }
    : listPicker;

  const popover = useAsyncSelectorPopover();
  const suggestions = usePagedAsyncSuggestions<TItem>({
    queryKey,
    search,
    enabled: popover.pickerActive,
    initialQuery: '',
    initialPage: 1,
    initialPageSize: defaultPageSize,
    filterResetKey,
  });

  const selection = useAsyncSelectorSelection({
    mode,
    selected,
    getKey,
    onChange,
    isItemSelected,
    resetSuggestions: suggestions.reset,
    closePopover: () => popover.setOpen(false),
  });

  const nav = useAsyncSelectorLegacyNav({
    objectCategory,
    form,
    listPicker: effectiveListPicker,
    mode,
    selected,
    getKey,
    onChange,
    resolveByIds,
    openPickModal: () => popover.setPickModalOpen(true),
    resetSuggestions: suggestions.reset,
  });

  const onChangeRef = useLatest(onChange);

  // + (nový záznam) i ⋯ (list page / výběrový modal) vedou MIMO popover — otevřou nad ním další
  // vrstvu, takže popover zavíráme. Jinak by visel nad zakládacím formulářem i výběrem.
  const setPopoverOpen = popover.setOpen;
  const { handleCreate, handleOpenList } = nav;
  const closeAndCreate = useCallback(() => {
    setPopoverOpen(false);
    handleCreate();
  }, [setPopoverOpen, handleCreate]);
  const closeAndOpenList = useCallback(() => {
    setPopoverOpen(false);
    handleOpenList();
  }, [setPopoverOpen, handleOpenList]);

  const [activeIdx, setActiveIdx] = useState(0);
  // button + multi: rozpracovaný draft výběr (čistí se při otevření, potvrzuje „Vybrat").
  const [working, setWorking] = useState<readonly TItem[]>([]);

  // Kotva (.phxsel) a popover (.phxsel-pop, portálovaný do body) pro viewport-aware měření.
  const anchorRef = useRef<HTMLDivElement>(null);
  const popRef = popover.popRef;
  // Trigger (pole / tlačítko) — kvůli návratu fokusu po zavření popoveru přes ESC.
  const triggerRef = useRef<HTMLElement | null>(null);
  const captureTrigger = useCallback((el: HTMLElement | null) => {
    triggerRef.current = el;
  }, []);
  const [placement, setPlacement] = useState<{
    v: 'down' | 'up';
    h: 'left' | 'right';
    listMax: number;
    style: CSSProperties;
  } | null>(null);

  const resolvedButtonLabel = buttonLabel ?? t('button.defaultLabel');
  const resolvedConfirmLabel = confirmLabel ?? t('footer.confirm');

  const workingIds = useMemo(() => new Set(working.map(getKey)), [working, getKey]);
  // Zamčené řádky (už v cílovém seznamu) — jen mimo addMode.
  const isLocked = useCallback(
    (item: TItem) => !addMode && !!lockedIds && lockedIds.has(getKey(item)),
    [addMode, lockedIds, getKey]
  );
  // Zaškrtnutí řádku popoveru: v draft módu dle draftu, jinak dle skutečného výběru.
  const isRowSelected = useCallback(
    (item: TItem) => (workingMode ? workingIds.has(getKey(item)) : selection.isSelected(item)),
    [workingMode, workingIds, getKey, selection]
  );

  useEffect(() => {
    setActiveIdx(0);
  }, [suggestions.query, popover.open, suggestions.items]);

  // Fokus jde při otevření popoveru automaticky do vyhledávání. Odkládá se přes rAF až
  // za první změření pozice — popover je do té doby visibility:hidden (viz HIDDEN_POPOVER_STYLE)
  // a fokus na skrytém prvku by v prohlížeči propadl.
  useEffect(() => {
    if (!popover.open) return;
    const raf = requestAnimationFrame(() => popover.inputRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [popover.open, popover.inputRef]);

  // Každé otevření draft popoveru začíná s prázdným výběrem.
  useEffect(() => {
    if (popover.open && workingMode) setWorking([]);
  }, [popover.open, workingMode]);

  // Viewport-aware umístění popoveru. Popover je portálovaný do document.body (aby
  // ležel NAD všemi okny PhoenixDesktopu), proto se pozicuje `position: fixed` podle
  // bounding rectu kotvy — zůstává „přilepený" pod/nad trigger elementem a přepočítává
  // se při scrollu / resize / změně obsahu. Zároveň se překlopí nahoru / zarovná doprava
  // a omezí výšku seznamu tak, aby se vždy celý vešel do viewportu. Měří se bez bliknutí
  // (useLayoutEffect, synchronně před vykreslením).
  useLayoutEffect(() => {
    if (!popover.open) {
      setPlacement(null);
      return;
    }
    const anchor = anchorRef.current;
    // Svislé ukotvení pole (řádky) visí pod spodkem PRVNÍHO řádku (výška jednoho
    // controlu), ne pod narostlým polem — čteme z CSS proměnné (konstantní).
    const controlPx =
      (anchor && parseFloat(getComputedStyle(anchor).getPropertyValue('--phx-control'))) || 26;
    const measure = () => {
      const pop = popRef.current;
      if (!anchor || !pop) return;
      const rect = anchor.getBoundingClientRect();
      const popH = pop.offsetHeight;
      const popW = pop.offsetWidth;
      const m = 8;

      // Bod, kde se popover dotýká triggeru: down = pod spodním okrajem tlačítka /
      // pod spodkem PRVNÍHO řádku pole; up = nad horním okrajem kotvy. Rozhodnutí
      // nahoru/dolů se měří proti VIDITELNÉMU VIEWPORTU (0…innerHeight) vůči tomuto
      // bodu — ne proti (narostlému) poli ani parent elementu triggeru.
      const downTop = isButton ? rect.bottom + 3 : rect.top + controlPx + 4;
      const upBottom = rect.top - 3;
      const spaceBelow = window.innerHeight - downTop;
      const spaceAbove = upBottom;
      const v: 'down' | 'up' = spaceBelow < popH + m && spaceAbove > spaceBelow ? 'up' : 'down';
      const avail = (v === 'up' ? spaceAbove : spaceBelow) - m * 2;
      const listMax = Math.max(132, Math.min(320, avail - 112));
      const h: 'left' | 'right' =
        rect.left + popW > window.innerWidth - m && rect.right - popW > m ? 'right' : 'left';

      const style: CSSProperties = {
        position: 'fixed',
        zIndex: 'var(--phx-z-overlay)',
        minWidth: rect.width,
        ...(v === 'down' ? { top: downTop } : { bottom: window.innerHeight - upBottom }),
        ...(h === 'left' ? { left: rect.left } : { right: window.innerWidth - rect.right }),
      };

      setPlacement((prev) =>
        prev &&
        prev.v === v &&
        prev.h === h &&
        prev.listMax === listMax &&
        prev.style.top === style.top &&
        prev.style.bottom === style.bottom &&
        prev.style.left === style.left &&
        prev.style.right === style.right &&
        prev.style.minWidth === style.minWidth
          ? prev
          : { v, h, listMax, style }
      );
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [
    popover.open,
    popRef,
    isButton,
    suggestions.query,
    suggestions.page,
    suggestions.items.length,
  ]);

  const totalPages = Math.max(1, Math.ceil(suggestions.totalCount / suggestions.pageSize));

  // Klik/Enter na řádek popoveru. Pole: single vybere (nahradí) a zavře, multi přepne.
  // Tlačítko: draft (multi) přepíná rozpracovaný výběr; single emituje dávku jednoho
  // záznamu — v addMode resetuje a nechá popover otevřený, jinak zavře.
  const pickItem = useCallback(
    (item: TItem) => {
      if (isLocked(item)) return;
      if (workingMode) {
        const key = getKey(item);
        setWorking((prev) =>
          prev.some((existing) => getKey(existing) === key)
            ? prev.filter((existing) => getKey(existing) !== key)
            : [...prev, item]
        );
        return;
      }
      if (isButton) {
        onChange([item]);
        suggestions.reset();
        if (addMode) {
          setActiveIdx(0);
        } else {
          popover.setOpen(false);
        }
        return;
      }
      if (mode === 'multi') {
        selection.handleToggleSelection(item);
        return;
      }
      if (selection.isSelected(item)) {
        suggestions.reset();
        popover.setOpen(false);
        return;
      }
      selection.handleSelect(item);
    },
    [
      isLocked,
      workingMode,
      getKey,
      isButton,
      onChange,
      addMode,
      mode,
      selection,
      suggestions,
      popover,
    ]
  );

  // Potvrzení draftu (button + multi): propíše dávku rodiči. V addMode vyprázdní draft
  // a nechá popover otevřený pro další dávku, jinak zavře.
  const confirmWorking = useCallback(() => {
    onChange(working);
    suggestions.reset();
    if (addMode) {
      setWorking([]);
      setActiveIdx(0);
    } else {
      popover.setOpen(false);
    }
  }, [onChange, working, suggestions, addMode, popover]);

  // ESC: zavři popover a vrať fokus zpět na trigger selectoru (pole / tlačítko). Vyhledávací
  // input v popoveru se odmountuje, fokus by jinak spadl na <body> — proto rAF až po unmountu.
  const closeAndRefocus = useCallback(() => {
    popover.setOpen(false);
    requestAnimationFrame(() => triggerRef.current?.focus());
  }, [popover]);

  const handleSearchKey = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      const items = suggestions.items;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIdx((idx) => Math.min(items.length - 1, idx + 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIdx((idx) => Math.max(0, idx - 1));
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const item = items[activeIdx];
        if (item) pickItem(item);
      } else if (event.key === 'Escape') {
        // ESC je scoped na popover — neprobubláva do rodiče (např. zavření modalu navigace).
        event.preventDefault();
        event.stopPropagation();
        closeAndRefocus();
      } else if (event.key === 'PageDown') {
        event.preventDefault();
        suggestions.setPage(Math.min(totalPages, suggestions.page + 1));
      } else if (event.key === 'PageUp') {
        event.preventDefault();
        suggestions.setPage(Math.max(1, suggestions.page - 1));
      } else if (
        event.key === 'Backspace' &&
        suggestions.query.length === 0 &&
        mode === 'multi' &&
        !isButton &&
        selected.length > 0
      ) {
        selection.handleRemove(selected[selected.length - 1]!);
      }
    },
    [
      suggestions,
      activeIdx,
      pickItem,
      closeAndRefocus,
      totalPages,
      mode,
      isButton,
      selected,
      selection,
    ]
  );

  const handleFieldKey = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      if (!popover.open && (event.key === 'ArrowDown' || event.key === 'Enter')) {
        event.preventDefault();
        popover.setOpen(true);
        return;
      }
      if (popover.open && event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeAndRefocus();
      }
    },
    [disabled, popover, closeAndRefocus]
  );

  const hasError = !!error;
  const messageId = `${fieldId}-message`;
  const isEmpty = selected.length === 0;
  // + Nový záznam — jen v prázdném stavu; ⋯ Otevřít seznam — vždy. Zamčené pole nemá ani jedno:
  // obojí by vedlo k zápisu.
  const showCreateBtn = nav.hasNavigation && isEmpty && !disabled;
  const showListBtn = nav.hasNavigation && !disabled;

  const isInline = layout === 'inline';

  return (
    <div
      className={
        // Button mód není formulářové pole (viz README §2) — nemá popisek ani mřížku pole, jinak by
        // prázdný řádek popisku roztáhl tlačítko nad jeho vlastní výšku (znát to je v hlavičce panelu).
        isButton
          ? 'flex min-w-0'
          : isInline
            ? 'phx-field gap-phx-4 flex min-w-0 items-start'
            : 'grid gap-1.5'
      }
      data-layout={isInline && !isButton ? 'inline' : undefined}
      data-testid={testId}
      ref={popover.wrapRef}
    >
      {/* `required` musí na label (hvězdička), ne jen na pole (žlutý podklad) — stejně jako ve `Field`. */}
      {isButton ? null : isInline ? (
        <InlineFieldLabel htmlFor={fieldId} required={required} width={labelWidth}>
          {label}
        </InlineFieldLabel>
      ) : (
        <FieldLabel htmlFor={fieldId} required={required}>
          {label}
        </FieldLabel>
      )}

      <div className={isInline && !isButton ? 'flex min-w-0 flex-1 flex-col gap-1.5' : 'contents'}>
        <div
          className="phxsel"
          ref={anchorRef}
          {...(isButton ? { 'data-trigger': 'button' } : { 'data-layout': 'rows' })}
          {...(popover.open ? { 'data-open': '' } : {})}
          {...(mode === 'multi' ? { 'data-multi': '' } : {})}
          {...(!isEmpty && !isButton ? { 'data-filled': '' } : {})}
          {...(required ? { 'data-required': '' } : {})}
          {...(hasError ? { 'data-error': '' } : {})}
          {...(disabled ? { 'data-disabled': '' } : {})}
        >
          {isButton ? (
            <Button
              ref={captureTrigger}
              variant={buttonVariant}
              size={buttonSize}
              className="phxsel-trigger"
              iconLeft="plus"
              disabled={disabled}
              aria-haspopup="listbox"
              aria-expanded={popover.open}
              aria-controls={popupId}
              {...(popover.open ? { 'data-open': '' } : {})}
              onClick={() => popover.setOpen(!popover.open)}
            >
              {resolvedButtonLabel}
            </Button>
          ) : (
            <AsyncSelectorField
              triggerRef={captureTrigger}
              mode={mode}
              fieldId={fieldId}
              popupId={popupId}
              open={popover.open}
              selected={selected}
              columns={columns}
              renderSelected={renderSelected}
              renderSelectedSecondary={renderSelectedSecondary}
              getKey={getKey}
              placeholder={placeholder ?? t('placeholder')}
              isPending={nav.isPending}
              disabled={disabled}
              showCreateBtn={showCreateBtn}
              showListBtn={showListBtn}
              onActivate={() => popover.setOpen(true)}
              onKeyDown={handleFieldKey}
              onCreate={closeAndCreate}
              onOpenList={closeAndOpenList}
              onEditItem={nav.hasNavigation ? nav.handleEditItem : undefined}
              onRemove={selection.handleRemove}
              onClearAll={selection.handleClear}
              createLabel={t('actions.create')}
              openListLabel={t('actions.openList')}
              editLabel={t('actions.edit')}
              removeLabel={t('actions.remove')}
              clearAllLabel={t('actions.clearAll')}
              ariaLabel={label}
              ariaDescribedBy={helperText || error ? messageId : undefined}
              ariaInvalid={hasError}
            />
          )}

          {popover.open && typeof document !== 'undefined'
            ? // Portál do document.body: popover leží NAD všemi okny PhoenixDesktopu
              // (z-index --phx-z-overlay) a žádný overflow/stacking context okna ho neořízne.
              createPortal(
                <AsyncSelectorPopover
                  mode={mode}
                  popupId={popupId}
                  columns={columns}
                  suggestions={suggestions}
                  getKey={getKey}
                  isSelected={isRowSelected}
                  isLocked={isLocked}
                  selectedCount={selected.length}
                  activeIdx={activeIdx}
                  onHoverRow={setActiveIdx}
                  onPickRow={pickItem}
                  searchRef={popover.inputRef}
                  onSearchKeyDown={handleSearchKey}
                  placeholder={placeholder ?? t('placeholder')}
                  searchAriaLabel={label}
                  onEditItem={nav.hasNavigation ? nav.handleEditItem : undefined}
                  editLabel={t('actions.edit')}
                  editDisabled={nav.isPending}
                  popRef={popRef}
                  vpos={placement?.v ?? 'down'}
                  halign={placement?.h ?? 'left'}
                  popStyle={placement?.style ?? HIDDEN_POPOVER_STYLE}
                  listMaxHeight={placement?.listMax}
                  isButton={isButton}
                  onCreate={isButton && nav.hasNavigation ? closeAndCreate : undefined}
                  onOpenList={isButton && nav.hasNavigation ? closeAndOpenList : undefined}
                  createLabel={t('actions.create')}
                  openListLabel={t('actions.openList')}
                  showConfirm={workingMode}
                  confirmLabel={resolvedConfirmLabel}
                  confirmCount={working.length}
                  onConfirm={confirmWorking}
                />,
                document.body
              )
            : null}
        </div>

        {typeof effectiveListPicker === 'object' ? (
          // ⋯ otevírá plnohodnotnou Phoenix ListPage ve výběrovém režimu; potvrzená id se
          // přeloží na záznamy přes resolveByIds (kruh selector ↔ listpage).
          <effectiveListPicker.listPage
            open={popover.pickModalOpen}
            onClose={() => popover.setPickModalOpen(false)}
            initialSelectedIds={selected.map(getKey)}
            onConfirm={(ids) => {
              popover.setPickModalOpen(false);
              if (!resolveByIds) return;
              void resolveByIds(ids)
                .then((items) => onChangeRef.current(mode === 'single' ? items.slice(0, 1) : items))
                .catch(() => undefined);
            }}
          />
        ) : (
          <AsyncSelectorPickModal
            open={popover.pickModalOpen}
            onClose={() => popover.setPickModalOpen(false)}
            initialSelected={selected}
            mode={mode}
            columns={columns}
            suggestions={suggestions}
            onConfirm={(items) => {
              onChangeRef.current(items);
              popover.setPickModalOpen(false);
            }}
            renderPickToolbar={renderPickToolbar}
            title={pickModalTitle}
            width={pickModalWidth}
            getKey={getKey}
          />
        )}

        {error || helperText ? (
          <FieldMessage id={messageId} variant={hasError ? 'error' : 'helper'}>
            {error ?? helperText}
          </FieldMessage>
        ) : null}
      </div>
    </div>
  );
}
